'use client';

import { Close } from '@radix-ui/react-dialog';
import { useEffect } from 'react';
import { Icon } from '@/app_components/common/Icon';
import { BasicModal } from '@/app_components/ui/BasicModal';
import { ButtonV2 } from '@/app_components/ui/ButtonV2';
import {
  VideoBase,
  VideoContent as VideoContentLayout,
  VideoDate,
  VideoDescription,
  VideoPlayer,
  VideoTitle,
} from '@/app_components/Video/Video';
import { VideoDefault } from '@/app_components/Video/VideoDefault';
import { useFirstClickedVideo } from '@/app_lib/hooks/useFirstClickedVideo';
import type { Video } from '@/app_lib/types/Video';

export const VideoContent = ({
  target,
  videos,
  currentVideoIndex,
  videoId,
  videoType = 'stn',
  containerRef,
  scrollContainerRef,
  dedupKey,
  videoUrl,
  onEnd,
}: {
  target: string;
  videos: Video[];
  currentVideoIndex: number;
  videoId?: string;
  videoType?: 'stn' | 'vod';
  dedupKey?: string;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
  videoUrl?: string | null;
  onEnd?: () => void;
}) => {
  const activeVideo = videos[currentVideoIndex];

  // biome-ignore lint/correctness/useExhaustiveDependencies: currentVideoIndex is an intentional trigger to scroll on video change
  useEffect(() => {
    scrollContainerRef?.current?.scrollTo?.({ behavior: 'smooth', top: 0 });
  }, [currentVideoIndex]);

  return (
    <div
      className='bg-black w-screen h-[95dvh] rounded-t-lg pb-default overflow-hidden'
      data-theme='dark'
      ref={containerRef}
    >
      <div className='text-primary layout-safe-area w-full flex flex-col h-full min-h-0'>
        <div className='flex justify-end w-full py-sm shrink-0'>
          <Close asChild>
            <ButtonV2
              as='button'
              isTertiary
              variant='secondary'
            >
              <span className='hidden md:inline'>Close</span>
              <Icon icon='X' />
            </ButtonV2>
          </Close>
        </div>
        <div
          className='flex flex-col lg:flex-row layout-grid-gap justify-start lg:justify-center overflow-y-auto lg:overflow-hidden min-h-0'
          ref={scrollContainerRef}
        >
          <VideoBase
            className='max-lg:flex-none'
            modal
            orientation='vertical'
          >
            <VideoPlayer
              dedupKey={dedupKey}
              layout='default'
              modal
              onEnd={onEnd}
              orientation='vertical'
              target={target}
              videoId={videoId ?? activeVideo.id}
              videoType={videoType}
              videoUrl={videoUrl}
            />
            <VideoContentLayout
              modal
              orientation='vertical'
            >
              <VideoTitle
                className='typography-level-6'
                modal
                orientation='vertical'
                playing
                title={activeVideo.title}
              />
              <VideoDescription
                description={activeVideo.summary}
                modal
                orientation='vertical'
              />
              <VideoDate
                date={activeVideo.age}
                modal
                orientation='vertical'
              />
            </VideoContentLayout>
          </VideoBase>
          <div className='flex flex-col layout-gap-xs w-full lg:w-[368px] lg:overflow-y-auto lg:min-h-0 lg:flex-none'>
            {videos.map((video, index) => (
              <VideoDefault
                data-index={index}
                key={`video-modal-${video.id}-${index}`}
                modal
                orientation='horizontal'
                target={target}
                video={video}
                videoType={videoType}
                watching={currentVideoIndex === index}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * VideoModal is a UI abstraction component for presenting video content
 * inside a modal dialog. It is intentionally designed to separate and store
 * the overall modal structure and interactive design outside of the
 * video playback functionality itself.
 *
 * This abstraction allows flexible injection of different types of video content
 * (via the `content` prop), and manages the experience and navigation state
 * for collections or carousels of videos, including opening/closing, video selection,
 * and delegating actual player composition elsewhere.
 *
 * @param target - CSS class or selector string used to identify video thumbnails in collections.
 * @param videos - Array of video metadata to display/select in the modal.
 * @param videoType - Which player integration to use ('stn' | 'vod'). Defaults to 'stn'.
 * @param initialVideoId - Optionally force-initializes the modal to a specific video.
 * @param open - Whether the modal is open.
 * @param setOpen - Callback to update modal open state.
 * @param currentVideoIndex - Index of the currently selected/playing video.
 * @param setCurrentVideoIndex - Callback to update the currently selected video.
 * @param content - (Optional) Pre-rendered modal content; if not provided, a default layout is used.
 *
 * This component only deals with orchestration and presentation design,
 * and does not directly handle video playback logic.
 */
export const VideoModal = ({
  target,
  videos,
  videoType = 'stn',
  initialVideoId,
  open,
  setOpen,
  currentVideoIndex,
  setCurrentVideoIndex,
  content: contentProp,
  onOpen,
  onClose,
}: {
  target: string;
  videos: Video[];
  videoType?: 'stn' | 'vod';
  initialVideoId?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  onOpen?: ({ index }: { index: number }) => Promise<void>;
  currentVideoIndex: number;
  setCurrentVideoIndex: (index: number) => void;
  content: React.ReactNode;
  onClose?: () => void;
}) => {
  const { firstClickedVideoId, setFirstClickedVideoId } = useFirstClickedVideo();

  const content = contentProp ?? (
    <VideoContent
      currentVideoIndex={currentVideoIndex}
      target={target}
      videoId={initialVideoId ?? firstClickedVideoId ?? undefined}
      videos={videos}
      videoType={videoType}
    />
  );

  // Handles click events on video thumbnails to control which video is
  // shown/selected in the modal. Delegates to modal controls if not open.
  useEffect(() => {
    const handleClick = async (event: MouseEvent) => {
      const el = (event.target as HTMLElement)?.closest(`.${target}`) as HTMLElement | null;

      if (!el) {
        return;
      }

      // get the data-index of the clicked video
      const index = el.getAttribute('data-index') ?? '0';
      const parsedIndex = Number.parseInt(index, 10);

      setFirstClickedVideoId(videos[parsedIndex]?.id ?? '0');

      if (!open) {
        if (onOpen) {
          await onOpen?.({ index: parsedIndex });
          return;
        }
        setOpen(true);
      }

      if (parsedIndex !== currentVideoIndex) {
        setCurrentVideoIndex(parsedIndex);
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [currentVideoIndex, open, target, videos, setFirstClickedVideoId, setOpen, setCurrentVideoIndex, onOpen]);

  return (
    <BasicModal
      content={content}
      onOpenChange={(newOpen) => {
        setOpen(newOpen);
        if (!newOpen) {
          void onClose?.();
        }
      }}
      openOverride={open}
      position='bottom'
    />
  );
};
